import React, { useState, useEffect, useMemo } from "react";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { Switch } from "../../../ui/switch";
import { Tooltip } from "../../../ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../../ui/alert-dialog";
import { 
  Trash2,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Tag as TagIcon,
} from "lucide-react";
import { cn } from "../../../../lib/utils";

interface PlaceholdersTabProps {
  nodes: any[];
  globalVariableRegistry: any;
  onUpdateGlobalVariableRegistry: (registry: any) => void;
  onNodeFocus?: (nodeId: string) => void;
}

const PlaceholdersTab: React.FC<PlaceholdersTabProps> = ({
  nodes,
  globalVariableRegistry,
  onUpdateGlobalVariableRegistry,
  onNodeFocus,
}) => {
  const [editingPlaceholder, setEditingPlaceholder] = useState<string | null>(null);
  const [newExampleValue, setNewExampleValue] = useState("");
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [placeholderToDelete, setPlaceholderToDelete] = useState<string | null>(null);

  // Memoize placeholder computation to avoid O(N) traversal on every render
  const allPlaceholders = useMemo(() => {
    const placeholders: Record<string, any> = { ...globalVariableRegistry };
    
    // Scan all nodes for placeholders that might not be in the global registry
    nodes.forEach((node: any) => {
      const content = node.data?.content || "";
      const regex = /{([^{}]+)}/g;
      let match;
      while ((match = regex.exec(content)) !== null) {
        const placeholderName = match[1];
        if (!placeholders[placeholderName]) {
          // Add placeholder that exists in nodes but not in global registry
          placeholders[placeholderName] = {
            name: placeholderName,
            values: [], // Changed from examples to values
            persist: false // Default persist to false
          };
        }
      }
      
      // Also check tool call parameters and response variables
      if (node.data?.toolCalls) {
        node.data.toolCalls.forEach((toolCall: any) => {
          // Check parameters for placeholders
          if (Array.isArray(toolCall.parameters)) {
            toolCall.parameters.forEach((param: any) => {
            if (param.value) {
              const toolCallRegex = /{([^{}]+)}/g;
              let tcMatch;
              while ((tcMatch = toolCallRegex.exec(param.value)) !== null) {
                const placeholderName = tcMatch[1];
                if (!placeholders[placeholderName]) {
                  placeholders[placeholderName] = {
                    name: placeholderName,
                    values: [],
                    persist: false
                  };
                }
              }
            }
          });
          }
          
          // Include response variables as placeholders
          if (Array.isArray(toolCall.responseVariables)) {
            toolCall.responseVariables.forEach((responseVar: any) => {
            if (responseVar.name && !placeholders[responseVar.name]) {
              placeholders[responseVar.name] = {
                name: responseVar.name,
                values: responseVar.values || [],
                persist: false
              };
            }
          });
          }
        });
      }
    });
    
    return placeholders;
  }, [nodes, globalVariableRegistry]);

  const handleDeletePlaceholder = (placeholderName: string) => {
    const updatedRegistry = { ...globalVariableRegistry };
    delete updatedRegistry[placeholderName];
    onUpdateGlobalVariableRegistry(updatedRegistry);
    setPlaceholderToDelete(null);
  };

  const handleAddExample = (placeholderName: string) => {
    const trimmedValue = newExampleValue.trim();
    if (!trimmedValue) return;
    
    const currentValues = globalVariableRegistry[placeholderName]?.values || [];
    
    // Check if example already exists to prevent duplicates
    if (currentValues.includes(trimmedValue)) {
      setNewExampleValue("");
      return;
    }
    
    const updatedRegistry = {
      ...globalVariableRegistry,
      [placeholderName]: {
        ...globalVariableRegistry[placeholderName],
        values: [...currentValues, trimmedValue] // Changed from examples to values
      }
    };
    
    onUpdateGlobalVariableRegistry(updatedRegistry);
    setNewExampleValue("");
  };

  const handleRemoveExample = (placeholderName: string, indexToRemove: number) => {
    const updatedRegistry = {
      ...globalVariableRegistry,
      [placeholderName]: {
        ...globalVariableRegistry[placeholderName],
        values: globalVariableRegistry[placeholderName]?.values?.filter((_: string, index: number) => index !== indexToRemove) || []
      }
    };
    
    onUpdateGlobalVariableRegistry(updatedRegistry);
  };

  // Handle persist toggle
  const handlePersistToggle = (placeholderName: string, persist: boolean) => {
    const updatedRegistry = {
      ...globalVariableRegistry,
      [placeholderName]: {
        ...globalVariableRegistry[placeholderName],
        persist
      }
    };
    
    onUpdateGlobalVariableRegistry(updatedRegistry);
  };

  // Function to find which nodes use a specific placeholder
  const getNodesUsingPlaceholder = (placeholderName: string) => {
    return nodes.filter((node: any) => {
      const content = node.data?.content || "";
      return content.includes(`{${placeholderName}}`);
    });
  };

  // Use effect to handle scanning for new placeholders and updating the registry
  useEffect(() => {
    // Check if there are any new placeholders not in the global registry
    const hasNewPlaceholders = Object.keys(allPlaceholders).some(
      key => !globalVariableRegistry[key]
    );
    
    if (hasNewPlaceholders) {
      onUpdateGlobalVariableRegistry(allPlaceholders);
    }
  }, [allPlaceholders, globalVariableRegistry, onUpdateGlobalVariableRegistry]);

  return (
    <div className="h-[calc(100vh-160px)] overflow-y-auto overflow-x-hidden p-6">
      {/* Existing Placeholders */}
      <div>
        {(() => {
          if (Object.keys(allPlaceholders).length === 0) {
            return (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="space-y-3">
                  <TagIcon className="w-12 h-12 text-muted-foreground mx-auto" />
                  <h3 className="text-lg font-gc-med text-foreground">
                    No placeholders found
                  </h3>
                  <p className="text-muted-foreground text-sm max-w-md">
                    Add placeholders like {"{name}"} to your node content to see them here
                  </p>
                </div>
              </div>
            );
          }
          
          return Object.entries(allPlaceholders).map(([placeholderName, placeholder]: [string, any]) => {
            const nodesUsing = getNodesUsingPlaceholder(placeholderName);
            const persist = placeholder.persist || false; // Default to false if not set
            
            const isExpanded = expandedNodes.includes(`nodes-${placeholderName}`);
            
            return (
              <div key={placeholderName}>
                <Card className="mb-4 border-border">
                  <CardContent className="p-3">
                    {/* Header row with placeholder name and controls */}
                    <div className="flex items-center justify-between mb-3">
                      {/* Left: Placeholder name and controls */}
                      <div className="flex items-center gap-2">
                        <Tooltip content={placeholderName} side="bottom">
                          <Badge 
                            variant="secondary" 
                            className="max-w-[200px] truncate text-xs font-gc-med"
                          >
                            {placeholderName}
                          </Badge>
                        </Tooltip>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground">Persist</span>
                          <Switch 
                            checked={persist}
                            onCheckedChange={(checked) => handlePersistToggle(placeholderName, checked)}
                          />
                          <Tooltip content="When enabled, this variable will keep the same value throughout the entire conversation. When disabled, the value can change at different points in the conversation." side="bottom" immediate={true}>
                            <div>
                              <HelpCircle className="w-3 h-3 text-maitai-green hover:text-maitai-green/80 transition-colors" />
                            </div>
                          </Tooltip>
                        </div>
                      </div>

                      {/* Right: Delete button and node count dropdown */}
                      <div className="flex items-center gap-1">
                        <AlertDialog open={placeholderToDelete === placeholderName} onOpenChange={(open) => !open && setPlaceholderToDelete(null)}>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                              onClick={() => setPlaceholderToDelete(placeholderName)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-lg font-gc-med text-foreground">
                                Delete Placeholder
                              </AlertDialogTitle>
                              <AlertDialogDescription className="text-sm font-gc-reg text-foreground">
                                Are you sure you want to delete the placeholder "{placeholderName}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="font-gc-med">
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction 
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90 font-gc-med"
                                onClick={() => handleDeletePlaceholder(placeholderName)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        
                        {/* Node count dropdown */}
                        {nodesUsing.length > 0 && (
                          <Collapsible
                            open={isExpanded}
                            onOpenChange={(open) => {
                              const key = `nodes-${placeholderName}`;
                              if (open) {
                                setExpandedNodes([...expandedNodes, key]);
                              } else {
                                setExpandedNodes(expandedNodes.filter(k => k !== key));
                              }
                            }}
                          >
                            <CollapsibleTrigger className="!flex !items-center !gap-1 !justify-start !w-auto text-xs text-muted-foreground hover:text-foreground transition-colors [&>svg]:!mr-0">
                              <span className="font-gc-med">{nodesUsing.length}</span>
                            </CollapsibleTrigger>
                          </Collapsible>
                        )}
                      </div>
                    </div>

                    {/* Values Section */}
                    <div className="border-t border-border pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-gc-med text-foreground">
                          Values ({placeholder.values?.length || 0})
                        </h4>
                      </div>
                      
                      {/* Add Example Input */}
                      <div className="flex gap-2 mb-2">
                        <Input 
                          value={editingPlaceholder === placeholderName ? newExampleValue : ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setEditingPlaceholder(placeholderName);
                            setNewExampleValue(e.target.value);
                          }}
                          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && handleAddExample(placeholderName)}
                          placeholder="Add example value"
                          className="flex-1 h-6 text-xs"
                        />
                        <Button 
                          onClick={() => handleAddExample(placeholderName)}
                          disabled={!newExampleValue.trim() || editingPlaceholder !== placeholderName}
                          size="sm"
                          className="h-6 px-2 text-xs bg-foreground hover:bg-foreground/90 text-background"
                        >
                          Add
                        </Button>
                      </div>
                      
                      {/* Example Tags */}
                      <div className="max-h-20 overflow-auto">
                        {placeholder.values?.length === 0 ? (
                          <p className="text-xs text-muted-foreground">
                            No values yet. Add some example values for this placeholder.
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {placeholder.values?.map((value: string, index: number) => (
                              <Badge 
                                key={`${value}-${index}`}
                                variant="outline"
                                className="text-xs border-maitai-green/30 text-maitai-green bg-maitai-green/10 hover:bg-maitai-green/20 transition-colors"
                              >
                                {value}
                                <button
                                  onClick={() => handleRemoveExample(placeholderName, index)}
                                  className="ml-1 hover:text-destructive transition-colors"
                                >
                                  ×
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Expanded node content */}
                    {nodesUsing.length > 0 && isExpanded && (
                      <div className="border-t border-border pt-3 mt-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-gc-med text-foreground">
                            Used in {nodesUsing.length} node{nodesUsing.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className="max-h-24 overflow-auto space-y-1">
                          {nodesUsing.map((node: any, index: number) => (
                            <Tooltip key={node.id} content="Click to focus on this node" side="left">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onNodeFocus?.(node.id)}
                                className="w-full justify-start text-left h-auto p-2 border border-border/50 bg-accent/20 hover:bg-accent/40"
                              >
                              <span className="font-gc-med text-maitai-green">#{index + 1}</span>
                                <span className="text-foreground ml-2">
                                  {node.data?.identifier || "Unclassified"}
                                </span>
                                <span className="text-muted-foreground text-[10px] ml-2">
                                  ({node.data?.entityType || "Unknown"})
                                </span>
                              </Button>
                            </Tooltip>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
};

export default PlaceholdersTab; 